/**
 * Clean up `additionalFields` on load: drop placements that duplicate another annotation's box,
 * and clear `linkedFieldId` where it cannot be true.
 *
 * An additional placement sitting at the EXACT box of any other annotation on the form — a
 * section field, a signer field, an initialer field, a signature or initials image, a subsequent
 * signing field — is the same rectangle published twice: once from its own collection and again
 * from `additionalFieldsJSON`. On the PDF it draws on top of the annotation it copies, so the
 * real one cannot be seen or selected. There is no case where two annotations legitimately
 * occupy the identical rectangle, so the duplicate is removed on load.
 *
 * Exact match only — page, x, y, width and height all equal. Coordinates are copied verbatim
 * when a duplicate is created, so a real duplicate always matches to the last decimal. A
 * placement nudged by a fraction of a point is NOT a duplicate: it is kept, and the annotation
 * it overlaps keeps rendering normally. Overlap is a drawing problem for an author to see and
 * fix, not something to silently delete.
 *
 * A duplicate is removed whether or not it carries `linkedFieldId`: the box is published twice
 * either way, and the flag only records which code created it.
 *
 * Orphan fields — `sectionId` null — do not contribute boxes. They never reach published
 * formData, and an "Additional field" binding legitimately sits at its own field's box, so
 * counting an orphan's box would delete the very placement that carries it.
 *
 * Nothing else is touched. Rows are not linked to fields, not moved, not renamed. An earlier
 * version of this module matched orphan rows to a section field by `attributeName` and stamped
 * `linkedFieldId` on them, which made every ownership rule in the editor treat unrelated
 * placements as that field's children: deleting the field deleted them all, dragging its box
 * rewrote them all to its coordinates, and the editor reported a plain section field as an
 * "Additional field" binding. Name matching is not ownership, so no link is derived at all.
 * `linkedFieldId` is now written only where it means something — by the Field Editor, for
 * bindings it creates.
 *
 * @param {Array} additionalFields - client additional rows
 * @param {Object} annotations - the other collections whose boxes must not be duplicated
 * @param {Array} [annotations.fields] - section fields
 * @param {Array} [annotations.signerAttributeBindings]
 * @param {Array} [annotations.initialerAttributeBindings]
 * @param {Array} [annotations.signatureAnnotations]
 * @param {Array} [annotations.initialsAnnotations]
 * @param {Array} [annotations.subsequentSigningAttributeBindings]
 * @returns {Array} additionalFields (same reference when nothing is dropped)
 */
export function sanitizeAdditionalPlacements(additionalFields, annotations = {}) {
  if (!Array.isArray(additionalFields) || additionalFields.length === 0) {
    return Array.isArray(additionalFields) ? additionalFields : [];
  }

  const boxes = new Set();
  for (const location of collectAnnotationBoxes(annotations)) {
    const key = boxKey(location);
    if (key) boxes.add(key);
  }
  if (boxes.size === 0) return additionalFields;

  // No exemption for `linkedFieldId`. The published bundle strips that property, but a Studio
  // revision stores editor state verbatim (models/StudioFormData.js), so a duplicate saved
  // earlier comes back from the cloud still marked as owned. Skipping owned rows would leave
  // exactly the duplicates that have been persisted the longest.
  const kept = additionalFields.filter((row) => {
    const key = boxKey(row?.pdfLocation);
    return !key || !boxes.has(key);
  });

  const linked = clearStaleBindingLinks(kept, annotations.fields);
  if (linked !== kept) return linked;
  return kept.length === additionalFields.length ? additionalFields : kept;
}

/**
 * Clear `linkedFieldId` where it cannot be true.
 *
 * A binding the Field Editor creates always sits at its field's own box — that is how
 * applyFieldPdfBindingFromEditor builds it. So a row whose box is NOT its linked field's box was
 * never a binding: the link was derived by name-matching in an older version of this module and
 * then persisted by a save. RF403 carries eleven of them, footer placements on pages 1-11 all
 * pointing at a section field on page 12.
 *
 * A stale link is not cosmetic. The editor reads it as an "Additional field" binding on a plain
 * section field, and the viewer hides that field's own box on the assumption a marker will draw
 * in its place — so the field disappears from the PDF entirely.
 *
 * A link to a field that no longer exists is cleared for the same reason.
 */
function clearStaleBindingLinks(rows, fields) {
  const boxByFieldId = new Map();
  for (const field of fields || []) {
    if (field?.id != null) boxByFieldId.set(field.id, boxKey(field.metadata?.pdfLocation));
  }

  let changed = false;
  const next = rows.map((row) => {
    const link = row?.linkedFieldId;
    if (link == null || link === "") return row;
    const fieldBox = boxByFieldId.get(link);
    // Same box → a real binding. Different box, or no such field → stale.
    if (fieldBox != null && fieldBox === boxKey(row.pdfLocation)) return row;
    changed = true;
    const { linkedFieldId, ...rest } = row;
    return rest;
  });
  return changed ? next : rows;
}

/** Every annotation rectangle on the form, from every collection that can carry one. */
function* collectAnnotationBoxes(annotations) {
  const {
    fields = [],
    signerAttributeBindings = [],
    initialerAttributeBindings = [],
    signatureAnnotations = [],
    initialsAnnotations = [],
    subsequentSigningAttributeBindings = [],
  } = annotations || {};

  for (const field of fields || []) {
    // An orphan (no section) is not a legitimate annotation — it is left-over state that never
    // reaches published formData. Counting its box would make the placement that legitimately
    // covers it look like a duplicate and delete it. Orphans are handled separately.
    if (field?.sectionId == null || field.sectionId === "") continue;
    const meta = field?.metadata;
    if (!meta) continue;
    yield meta.pdfLocation;
    // A field's box is not always its only rectangle: options, the "other" input and overflow
    // continuations each place one, and a duplicate can land on any of them.
    for (const option of meta.options || []) yield option?.pdfLocation;
    yield meta.other?.input?.pdfLocation;
    yield meta.other?.checkbox?.pdfLocation;
    yield meta.other?.radio?.pdfLocation;
    for (const overflow of meta.overflow || []) yield overflow?.pdfLocation;
  }

  // Binding collections all keep the rectangle at the top level, matching how each is built in App.js.
  for (const row of signerAttributeBindings || []) yield row?.pdfLocation;
  for (const row of initialerAttributeBindings || []) yield row?.pdfLocation;
  for (const row of signatureAnnotations || []) yield row?.pdfLocation;
  for (const row of initialsAnnotations || []) yield row?.pdfLocation;
  for (const row of subsequentSigningAttributeBindings || []) yield row?.pdfLocation;
}

/** Identity of a rectangle. Null for anything without a page, so partial boxes never match. */
function boxKey(location) {
  if (!location || location.page == null) return null;
  const { page, x, y, width, height } = location;
  if (x == null || y == null || width == null || height == null) return null;
  return `${page}|${x}|${y}|${width}|${height}`;
}

/**
 * Coincident boxes that an analyst should look at, with a note when the dropped placement looks
 * like the wrong casualty.
 *
 * A placement and another annotation at the identical box means one of them is wrong, and geometry
 * cannot say which. Two shapes produce it:
 *   - a stray placement on a correctly-placed field — dropping the placement is right,
 *   - a MISPLACED FIELD parked on a legitimate placement — dropping the placement destroys real
 *     data and the field is what needs moving.
 *
 * RF403 is the second. Its section field sits in the page-12 footer slot, on top of the page-12
 * footer placement, while eleven sibling placements occupy that same slot on pages 1-11. The
 * sibling pattern is the tell: when the dropped placement matches the slot its siblings use, it
 * belongs there and the field is the odd one out.
 *
 * @returns {Array} { rowId, fieldId, attributeName, page, box, siblingCount, review }
 */
export function findCoincidentPlacements(additionalFields, annotations = {}) {
  const rows = Array.isArray(additionalFields) ? additionalFields : [];
  const fields = (annotations.fields || []).filter((f) => f && f.sectionId != null && f.sectionId !== "");

  const fieldByBox = new Map();
  for (const field of fields) {
    const key = boxKey(field.metadata?.pdfLocation);
    if (key && !fieldByBox.has(key)) fieldByBox.set(key, field);
  }

  const out = [];
  for (const row of rows) {
    const key = boxKey(row?.pdfLocation);
    if (!key) continue;
    const field = fieldByBox.get(key);
    if (!field) continue;

    // Siblings: same attributeName, different page, within a couple of points of this slot.
    const loc = row.pdfLocation;
    const siblings = rows.filter(
      (r) =>
        r !== row &&
        r.attributeName === row.attributeName &&
        r.pdfLocation &&
        r.pdfLocation.page !== loc.page &&
        Math.abs(r.pdfLocation.x - loc.x) <= 2 &&
        Math.abs(r.pdfLocation.y - loc.y) <= 2,
    );

    out.push({
      rowId: row.id,
      fieldId: field.id,
      fieldLabel: field.label || field.attributeName || field.id,
      attributeName: row.attributeName ?? "",
      page: loc.page,
      box: `${loc.page} / ${Number(loc.x).toFixed(3)} / ${Number(loc.y).toFixed(3)}`,
      siblingCount: siblings.length,
      // Three or more siblings in the same slot on other pages means this placement is part of a
      // repeated element (a footer). It belongs where it is; the FIELD is what is misplaced.
      review: siblings.length >= 3,
    });
  }
  return out;
}
